// frontend/src/pages/admin/DashboardPage.js
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { FaBoxOpen, FaShoppingBag, FaUsers, FaList, FaExclamationTriangle, FaInfoCircle, FaWarehouse} from 'react-icons/fa';
import api from '../../utils/api';
import Loader from '../../components/ui/Loader';
import Message from '../../components/ui/Message';

const DashboardPage = () => {
  const [lowStockCount, setLowStockCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useSelector(state => state.auth);
  const isSuperAdmin = user?.is_super_admin || false;
  const [stats, setStats] = useState({
    productsCount: 0,
    ordersCount: 0,
    usersCount: 0,
    categoriesCount: 0,
    lowStockProducts: [],
    recentOrders: []
  });
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        
        // Get token from localStorage
        const token = localStorage.getItem('token');
        
        try {
          const lowStockResponse = await api.get('/inventory/alerts');
          const lowStockProducts = lowStockResponse.data;
          setLowStockCount(lowStockProducts.length);
        } catch (error) {
          console.error('Error fetching low stock products:', error);
        }

        if (!token) {
          setError('未授权，请登录');
          setLoading(false);
          return;
        }
        
        // In a real app, you'd have a dedicated dashboard API endpoint
        // Here we'll make multiple requests to get the data we need
        
        // Get product count and low stock products
        const productsRes = await api.get('/products');
        const productsCount = productsRes.data.pagination.total;
        
        // Get low stock products (stock < 10)
        const lowStockProducts = productsRes.data.products.filter(
          product => product.stock_quantity < 10
        );
        
        // Get orders
        const ordersRes = await api.get('/orders/admin');
        const ordersCount = ordersRes.data.pagination.total;
        const recentOrders = ordersRes.data.orders.slice(0, 5);
        
        // Get categories
        const categoriesRes = await api.get('/categories');
        const categoriesCount = categoriesRes.data.length;
        
        // Get users count
        const usersRes = await api.get('/users');
        const usersCount = usersRes.data.length;
        
        setStats({
          productsCount,
          ordersCount,
          usersCount,
          categoriesCount,
          lowStockProducts,
          recentOrders
        });
        
        setLoading(false);
      } catch (error) {
        setError(
          error.response && error.response.data.message
            ? error.response.data.message
            : '获取数据时出错'
        );
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate()
    ).padStart(2, '0')}`;
  };
  
  const getStatusClass = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return '待处理';
      case 'processing':
        return '处理中';
      case 'shipped':
        return '已发货';
      case 'delivered':
        return '已送达';
      case 'cancelled':
        return '已取消';
      default:
        return status;
    }
  };
  
  return (
    <div className="admin-dashboard">
      <h1 className="text-3xl font-bold mb-6">管理面板</h1>
      
      {loading ? (
        <Loader />
      ) : error ? (
        <Message variant="error">{error}</Message>
      ) : (
        <>
          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center">
                <div className="rounded-full bg-blue-100 p-3 mr-4">
                  <FaBoxOpen className="text-blue-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-500">产品总数</h3>
                  <p className="text-2xl font-bold">{stats.productsCount}</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center">
                <div className="rounded-full bg-green-100 p-3 mr-4">
                  <FaShoppingBag className="text-green-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-500">订单总数</h3>
                  <p className="text-2xl font-bold">{stats.ordersCount}</p>
                </div>
              </div>
            </div>
            
            <Link to="/admin/users" className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center">
                <div className="rounded-full bg-purple-100 p-3 mr-4">
                  <FaUsers className="text-purple-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-500">用户总数</h3>
                  <p className="text-2xl font-bold">{stats.usersCount}</p>
                </div>
              </div>
            </Link>
            
            {isSuperAdmin ? (
              <Link to="/admin/inventory" className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
                <div className="flex items-center">
                  <div className="rounded-full bg-yellow-100 p-3 mr-4">
                    <FaWarehouse className="text-yellow-600 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-500">低库存产品</h3>
                    <p className="text-2xl font-bold">{lowStockCount}</p>
                  </div>
                </div>
              </Link>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center">
                  <div className="rounded-full bg-gray-100 p-3 mr-4">
                    <FaWarehouse className="text-gray-400 text-xl" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-500">库存预警</h3>
                    <p className="text-2xl font-bold text-gray-400">***</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center">
                <div className="rounded-full bg-red-100 p-3 mr-4">
                  <FaList className="text-red-600 text-xl" />
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-500">分类总数</h3>
                  <p className="text-2xl font-bold">{stats.categoriesCount}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Recent Orders */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="border-b bg-gray-50 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-semibold">最近订单</h2>
                <Link
                  to="/admin/orders"
                  className="text-sm text-blue-600 hover:underline"
                >
                  查看全部
                </Link>
              </div>
              
              <div className="p-6">
                {stats.recentOrders.length === 0 ? (
                  <p className="text-gray-500">暂无订单</p>
                ) : (
                  <div className="space-y-4">
                    {stats.recentOrders.map((order) => (
                      <div key={order.id} className="border-b pb-4 last:border-b-0 last:pb-0">
                        <div className="flex justify-between items-center">
                          <div>
                            <Link
                              to={`/admin/orders/${order.id}`}
                              className="font-medium hover:text-red-700"
                            >
                              订单 #{order.id}
                            </Link>
                            <p className="text-sm text-gray-500">{formatDate(order.created_at)}</p>
                          </div>
                          <div>
                            <span
                              className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(
                                order.status
                              )}`}
                            >
                              {getStatusText(order.status)}
                            </span>
                          </div>
                        </div>
                        <div className="mt-2 flex justify-between">
                          <span className="text-sm">{order.full_name || order.phone_number}</span>
                          <span className="font-medium">¥{parseFloat(order.total_amount).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Low Stock Products */}
            <div className="bg-white rounded-lg shadow-md overflow-hidden">
              <div className="border-b bg-gray-50 px-6 py-4 flex justify-between items-center">
                <h2 className="text-xl font-semibold flex items-center">
                  <FaExclamationTriangle className="text-yellow-500 mr-2" />
                  库存预警
                </h2>
                <Link
                  to="/admin/products"
                  className="text-sm text-blue-600 hover:underline"
                >
                  查看全部产品
                </Link>
              </div>
              
              <div className="p-6">
                {stats.lowStockProducts.length === 0 ? (
                  <p className="text-gray-500">没有低库存产品</p>
                ) : (
                  <div className="space-y-4">
                    {stats.lowStockProducts.map((product) => (
                      <div key={product.id} className="flex items-center border-b pb-4 last:border-b-0 last:pb-0">
                        <div className="w-10 h-10 mr-4">
                          <img
                            src={product.image_url || '/images/default-wine.jpg'}
                            alt={product.name}
                            className="w-full h-full object-cover rounded"
                          />
                        </div>
                        <div className="flex-1">
                          <Link
                            to={`/admin/product/${product.id}`}
                            className="font-medium hover:text-red-700"
                          >
                            {product.name}
                          </Link>
                          {product.chinese_name && (
                            <p className="text-xs text-gray-500">{product.chinese_name}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <span className={`font-medium ${product.stock_quantity === 0 ? 'text-red-600' : 'text-yellow-600'}`}>
                            {product.stock_quantity === 0 ? '缺货' : `${product.stock_quantity} 件`}
                          </span>
                          <p className="text-xs text-gray-500">¥{parseFloat(product.price).toFixed(2)}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DashboardPage;